import { createContext, useEffect, useState, useRef, useContext, useCallback } from 'react';
import { IPCRendererCallback, IpcStatusType, IpcShellContextType, IpcShellProviderProps, shell } from './adapter';
import { useAppInfoStore } from './zustand-relay/stores/app-info-store';

const syncEventSlug = 'ipc:connection:sync';
const handshakeEventSlug = 'ipc:app:handshake';

/** Create Context with Initial State */
const ipcShellContext = createContext<IpcShellContextType>({
  status: 'waiting',
  version: undefined,
  resetConnection: () => {},
  loading: false,
});

export const IpcShellProvider = ({ context, children }: IpcShellProviderProps) => {
  const [status, setStatus] = useState<IpcStatusType>('waiting');
  const [loading, setLoading] = useState<boolean>(false);
  const { version } = useShellVersionCheck(); // Check Version & Check for updates

  const initialLoad = useRef<boolean>(true);

  const handleIpcConnectionSync: IPCRendererCallback<'ipc:connection:sync'> = useCallback((_e, { ipcStatus }) => {
    setStatus((prevStatus) => (prevStatus !== ipcStatus ? ipcStatus : prevStatus));
  }, []);

  const handleIpcAppHandshake: IPCRendererCallback<'ipc:app:handshake'> = useCallback(() => {
    shell.emit?.(handshakeEventSlug, { context });
  }, [context]);

  const fetchInitialStatus = useCallback(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      shell
        .invoke?.('ipc:get:status:state', { context })
        .then((incomingStatus) => {
          if (incomingStatus) {
            setStatus((prevStatus) => {
              if (prevStatus !== incomingStatus) {
                shell.emit?.(handshakeEventSlug, { context, ipcStatus: incomingStatus });
                return incomingStatus;
              }
              return prevStatus;
            });
          }
        })
        .catch((error) => console.error('[IPC Provider] Failed to get initial state:', error));
    }
  }, [context]);

  useEffect(() => {
    if (!shell.isShell) {
      if (status !== 'not_applicable') setStatus('not_applicable');
      return;
    }

    fetchInitialStatus();

    const syncStatusID = `${syncEventSlug}:${context}`;
    const handshakeID = `${handshakeEventSlug}:${context}`;

    shell.on?.(syncEventSlug, handleIpcConnectionSync, syncStatusID);
    shell.on?.(handshakeEventSlug, handleIpcAppHandshake, handshakeID);
    return () => {
      shell.off?.(syncEventSlug, handleIpcConnectionSync, syncStatusID);
      shell.off?.(handshakeEventSlug, handleIpcAppHandshake, handshakeID);
      // Cleanup any pending intervals or timeouts
      if (initialLoad.current) initialLoad.current = false;
    };
  }, [handleIpcAppHandshake, handleIpcConnectionSync, fetchInitialStatus, context, status]);

  /* Reset IPC Connection & Reload The Window*/
  const resetConnection = async () => {
    if (!shell.isShell) return;
    setLoading(true);
    setStatus('waiting');
    try {
      const res = await shell.invoke?.('ipc:connection:restart', undefined);
      console.log('IPC Connection Restarted:', res);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Failed to restart IPC Connection:', error);
    }
  };

  return (
    <ipcShellContext.Provider value={{ version, status, loading, resetConnection }}>
      {children}
    </ipcShellContext.Provider>
  );
};

export const useIpcShellProvider = () => useContext(ipcShellContext);

const useShellVersionCheck = () => {
  const version = useAppInfoStore((s) => s.version);

  useEffect(() => {
    if (version) {
      shell.version = version;
    }
  }, [version]);

  return {
    version,
  };
};
